import { z } from 'zod';
import { USER_ROLE, type UserRole } from './constants.ts';

/**
 * Der angemeldete Nutzer, wie ihn Server und Oberfläche sehen.
 *
 * `role` ist die fachliche Rolle (aus den Entra-Gruppen, ggf. überschrieben).
 * `isSysadmin` ist ein davon getrenntes Flag: Systemverwaltung gibt KEINE
 * fachlichen Rechte (Plan §7, Mitbestimmung). Wer beides braucht, steht in
 * beiden Gruppen.
 */
export const sessionUserSchema = z.object({
  id: z.uuid(),
  /** Lokale Konten (Werkstatt ohne Microsoft 365) haben nicht immer eine Mailadresse. */
  email: z.string().nullable(),
  displayName: z.string(),
  /** `entra` = Microsoft 365, `local` = Benutzername + Passwort. */
  authSource: z.enum(['entra', 'local']),
  /** Lokales Konto mit Erstpasswort: erst ein eigenes Passwort festlegen. */
  mustChangePassword: z.boolean(),
  role: z.enum(USER_ROLE),
  isSysadmin: z.boolean(),
  roleOverridden: z.boolean(),
  /** Standorte des Nutzers (`user_site`); steuert Warteschlange und Digest. */
  siteIds: z.array(z.uuid()),
  primarySiteId: z.uuid().nullable(),
  /** Gesetzt, wenn die Sitzung an einem registrierten Hallen-Tablet läuft. */
  deviceId: z.uuid().nullable(),
  /** Leerlauffrist dieser Tablet-Sitzung; ohne Tablet `null`, dort wacht nur der Server. */
  deviceIdleMinutes: z.number().int().positive().nullable(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

const accountNameSchema = z.string().trim().min(2, 'Bitte den Namen angeben.').max(80, 'Der Name ist zu lang.');
const accountEmailSchema = z.email('Bitte eine gültige E-Mail-Adresse angeben.').toLowerCase().nullable().optional();

export const updateUserSchema = z
  .object({
    /** `null` entfernt die manuelle Rolle – danach zählen die Entra-Gruppen. */
    roleOverride: z.enum(USER_ROLE).nullable().optional(),
    sysadminOverride: z.boolean().nullable().optional(),
    /** `null` entfernt die Standort-Ausnahme; sonst ersetzt die Liste alle Override-Zeilen. */
    siteOverrideIds: z.array(z.uuid()).nullable().optional(),
    primarySiteId: z.uuid().nullable().optional(),
    isActive: z.boolean().optional(),
    /** Name und Adresse nur bei lokalen Konten: Microsoft-Konten bekommen sie bei jeder Anmeldung aus Entra. */
    displayName: accountNameSchema.optional(),
    email: accountEmailSchema,
    /** Freiwillig; steht als Notiz im Audit-Log (Vorher/Nachher steht dort immer). */
    reason: z.string().trim().max(500).optional(),
  })
  .strict();
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const profileUpdateSchema = z
  .object({
    notifyEmail: z.boolean().optional(),
    notifyPush: z.boolean().optional(),
  })
  .strict();

/** Darf der Nutzer Tickets bearbeiten (annehmen, Status wechseln, abschließen)? */
export function canWork(user: { role: UserRole } | null): boolean {
  return user?.role === 'technician' || user?.role === 'lead';
}

export function isLead(user: { role: UserRole } | null): boolean {
  return user?.role === 'lead';
}

/**
 * Kann das Konto überhaupt Mail bekommen? Ein Melder ohne Microsoft 365 nie, auch nicht mit
 * hinterlegter Adresse, etwa nach dem Wechsel aus der Instandhaltung. Die Empfänger der
 * Benachrichtigungen (`usersWhere`) richten sich nach dieser Regel, das Profil ebenso.
 */
export function hasMailChannel(user: Pick<SessionUser, 'email' | 'authSource' | 'role'>): boolean {
  return Boolean(user.email) && !(user.authSource === 'local' && user.role === 'reporter');
}

/**
 * Sieht der Nutzer die Warteschlange dieses Standorts?
 * Leitung sieht alle; Instandhalter nur ihre `user_site`-Standorte (F2).
 */
export function canSeeSite(
  user: { role: UserRole; siteIds: string[] } | null,
  siteId: string,
): boolean {
  if (!user) return false;
  if (user.role === 'lead') return true;
  if (user.role === 'technician') return user.siteIds.includes(siteId);
  return false;
}

/** Initialen für das Namenskürzel – kein Profilfoto in V1. */
export function initials(name: string): string {
  // Nur Wörter mit Buchstaben – „(Admin)“ oder „–“ ergeben keine Initialen.
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => /^\p{L}/u.test(p));
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// ---------------------------------------------------------------------------
// Lokale Konten (ohne Microsoft 365)
// ---------------------------------------------------------------------------

/** Steht im Formular und in der Zod-Meldung, damit beide dieselbe Regel nennen. */
export const USERNAME_RULE = '3 bis 40 Zeichen aus Kleinbuchstaben ohne Umlaute, Ziffern, Punkt, Bindestrich oder Unterstrich, am Anfang ein Buchstabe oder eine Ziffer.';

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9._-]{2,39}$/, `Benutzername: ${USERNAME_RULE}`);

export const PASSWORD_MIN = 8;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `Das Passwort braucht mindestens ${PASSWORD_MIN} Zeichen.`)
  .max(128, 'Das Passwort ist zu lang.');

export const localLoginSchema = z.object({
  username: z.string().trim().toLowerCase().min(1, 'Benutzername fehlt.').max(40),
  password: z.string().min(1, 'Passwort fehlt.').max(128),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});

export const createLocalUserSchema = z.object({
  displayName: accountNameSchema,
  username: usernameSchema,
  email: accountEmailSchema,
  role: z.enum(USER_ROLE).default('reporter'),
  siteIds: z.array(z.uuid()).min(1, 'Bitte mindestens einen Standort wählen.'),
  primarySiteId: z.uuid().nullable().optional(),
  teamIds: z.array(z.uuid()).default([]),
});
export type CreateLocalUserInput = z.infer<typeof createLocalUserSchema>;
